import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiHandler } from '../../api/apihandler';
import { NO_PEANUT_POLICY_LIST, NO_PEANUT_POLICY_SUBMIT } from '../../api/constants';

// Fetch No Peanut Policies
export const fetchNoPeanutPolicies = createAsyncThunk(
  'noPeanutPolicy/fetchPolicies',
  async ({ page = 1, limit = 10, student_id }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('page', page);
      formData.append('limit', limit);
      if (student_id) {
        formData.append('student_id', student_id);
      }

      const response = await apiHandler(NO_PEANUT_POLICY_LIST, 'POST', formData);
      // API structure: response.data.data.data contains {data: [...], meta: {...}}
      return response.data.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch no peanut policies');
    }
  }
);

// Submit No Peanut Policy
export const submitNoPeanutPolicy = createAsyncThunk(
  'noPeanutPolicy/submitPolicy',
  async (policyData, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      Object.keys(policyData).forEach(key => {
        if (policyData[key] !== null && policyData[key] !== undefined) {
          formData.append(key, policyData[key]);
        }
      });
      
      const response = await apiHandler(NO_PEANUT_POLICY_SUBMIT, 'POST', formData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to submit no peanut policy');
    }
  }
);

const noPeanutPolicySlice = createSlice({
  name: 'noPeanutPolicy',
  initialState: {
    policies: [],
    meta: null,
    loading: false,
    error: null,
    submitLoading: false,
    submitError: null
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch Policies
      .addCase(fetchNoPeanutPolicies.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNoPeanutPolicies.fulfilled, (state, action) => {
        state.loading = false;
        state.policies = Array.isArray(action.payload?.data) ? action.payload.data : [];
        state.meta = action.payload?.meta || null;
      })
      .addCase(fetchNoPeanutPolicies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Submit Policy
      .addCase(submitNoPeanutPolicy.pending, (state) => {
        state.submitLoading = true;
        state.submitError = null;
      })
      .addCase(submitNoPeanutPolicy.fulfilled, (state, action) => {
        state.submitLoading = false;
        const newPolicy = action.payload;
        
        // If updating existing policy
        if (newPolicy?.id) {
          const index = state.policies.findIndex(p => p.id === newPolicy.id);
          if (index !== -1) {
            state.policies[index] = newPolicy;
          } else {
            state.policies.unshift(newPolicy);
          }
        } else if (newPolicy) {
          state.policies.unshift(newPolicy);
        }
      })
      .addCase(submitNoPeanutPolicy.rejected, (state, action) => {
        state.submitLoading = false;
        state.submitError = action.payload;
      });
  }
});

// Selectors
export const selectPolicies = (state) => state.noPeanutPolicy.policies;
export const selectMeta = (state) => state.noPeanutPolicy.meta;
export const selectLoading = (state) => state.noPeanutPolicy.loading;
export const selectError = (state) => state.noPeanutPolicy.error;
export const selectSubmitLoading = (state) => state.noPeanutPolicy.submitLoading;
export const selectSubmitError = (state) => state.noPeanutPolicy.submitError;
export const selectPolicyById = (state, policyId) => {
  return state.noPeanutPolicy.policies.find(policy => policy.id === parseInt(policyId));
};

export default noPeanutPolicySlice.reducer;
